import type { MemberListItem, MemberListQuery, MemberListResponse } from '@ai-meeting/shared'
import { apiClient, toApiError } from './api'
import { getServiceStrings } from './i18n'

const MEMBER_PAGE_SIZE = 100

export async function fetchMembers(query: MemberListQuery = {}): Promise<MemberListResponse> {
  try {
    const { data } = await apiClient.get<MemberListResponse>('/api/users/members', {
      params: query,
    })
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().membersLoadError)
  }
}

/**
 * Loads the complete app-user directory while keeping the backend endpoint
 * paginated. The first request discovers the total; independent remaining
 * pages are then fetched in parallel to avoid a request waterfall.
 */
export async function fetchAllMembers(
  query: Pick<MemberListQuery, 'search'> = {}
): Promise<MemberListItem[]> {
  const firstPage = await fetchMembers({
    ...query,
    page: 1,
    limit: MEMBER_PAGE_SIZE,
  })
  const pageCount = Math.ceil(firstPage.total / MEMBER_PAGE_SIZE)

  const remainingPages = await Promise.all(
    Array.from({ length: Math.max(pageCount - 1, 0) }, (_, index) =>
      fetchMembers({
        ...query,
        page: index + 2,
        limit: MEMBER_PAGE_SIZE,
      })
    )
  )

  const membersById = new Map<string, MemberListItem>()
  for (const page of [firstPage, ...remainingPages]) {
    for (const member of page.items) membersById.set(member.id, member)
  }
  return [...membersById.values()]
}
